import { zodResolver } from "@hookform/resolvers/zod"
import { Box, DialogActions, DialogContent, TextField, Typography } from "@mui/material"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { INVENTORY_ADJUSTMENT_TYPE_OPTIONS } from "./constants"
import { type InventoryAdjustmentLaunchFormValues, inventoryAdjustmentLaunchFormSchema } from "./schemas"

import { Button } from "@/components/common/Button"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { InventoryAdjustmentType, type Product } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface InventoryAdjustmentLaunchStepProps {
  product: Product
  warehouseOptions: Option<string>[]
  onCancel: () => void
  onContinue: (warehouseAreaId: string, adjustmentType: InventoryAdjustmentType) => void
}

export const InventoryAdjustmentLaunchStep: FC<InventoryAdjustmentLaunchStepProps> = ({
  product,
  warehouseOptions,
  onCancel,
  onContinue,
}) => {
  const methods = useForm<InventoryAdjustmentLaunchFormValues>({
    resolver: zodResolver(inventoryAdjustmentLaunchFormSchema),
    defaultValues: {
      warehouseAreaId: warehouseOptions[0]?.value ?? "",
      adjustmentType: InventoryAdjustmentType.AdjustQuantity,
    },
  })

  const { control, handleSubmit, watch } = methods
  const selectedAdjustmentType = watch("adjustmentType")

  const submitForm = (formValues: InventoryAdjustmentLaunchFormValues) => {
    onContinue(formValues.warehouseAreaId, formValues.adjustmentType as InventoryAdjustmentType)
  }

  return (
    <FormProvider {...methods}>
      <DialogContent>
        <Box className='flex flex-col gap-y-6 py-2'>
          <TextField
            label='Product'
            value={product.companyProductDescription}
            disabled
            fullWidth
            variant='outlined'
            data-testid='inventory-adjustment.launch-step.product-field'
          />
          <FormSelect
            control={control}
            name='warehouseAreaId'
            label='Warehouse'
            options={warehouseOptions}
            data-testid='inventory-adjustment.launch-step.warehouse-select'
          />
          <Box>
            <FormSelect
              control={control}
              name='adjustmentType'
              label='Adjustment Type'
              required
              options={INVENTORY_ADJUSTMENT_TYPE_OPTIONS}
              data-testid='inventory-adjustment.launch-step.adjustment-type-select'
            />
            {selectedAdjustmentType === InventoryAdjustmentType.AdjustQuantity && (
              <Typography variant='body2' className='mt-2 text-gray-600'>
                Select one or more pallets to adjust quantities.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className='justify-between px-6 pb-6'>
        <Button appearance='outlined' onClick={onCancel} data-testid='inventory-adjustment.launch-step.cancel-button'>
          Cancel
        </Button>
        <Button
          appearance='contained'
          onClick={handleSubmit(submitForm)}
          data-testid='inventory-adjustment.launch-step.continue-button'
        >
          Continue
        </Button>
      </DialogActions>
    </FormProvider>
  )
}
